import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, AlertCircle, Lock } from 'lucide-react';
import { useAuth } from '../App';
import BrandLogo from '../components/BrandLogo';
import toast from 'react-hot-toast';
import './Auth.css';
import './AdminLogin.css';

const AdminLogin = () => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    beginAdminLogin,
    verifyAdminLoginTwoFactor,
    startAdminPasswordReset,
    completeAdminPasswordReset,
    admin
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) {
      navigate('/admin');
    }
  }, [admin, navigate]);

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError('');
  };

  const handleLoginStart = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setIsLoading(true);
      const response = await beginAdminLogin({ email: email.trim(), password });
      setIsLoading(false);
      setChallengeToken(response.challengeToken || '');
      setSetup(response.requiresTwoFactorSetup ? response.setup || null : null);
      setCode('');
      switchMode('twoFactor');
      if (response.requiresTwoFactorSetup) {
        toast.success('Set up 2FA and enter current code to finish login');
      } else {
        toast.success('Enter your 2FA code to complete login');
      }
    } catch (submitError) {
      setIsLoading(false);
      setError(submitError?.message || 'Login failed');
    }
  };

  const handleTwoFactorVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (!challengeToken || !code.trim()) {
      setError('2FA code is required');
      return;
    }

    setIsLoading(true);
    const success = await verifyAdminLoginTwoFactor({
      challengeToken,
      code: code.trim(),
      email: email.trim()
    });
    setIsLoading(false);

    if (success) {
      toast.success('Welcome to Admin Panel');
      navigate('/admin');
      return;
    }

    setError('Invalid 2FA code or expired challenge');
  };

  const handleResetStart = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Owner email is required');
      return;
    }

    try {
      setIsLoading(true);
      const response = await startAdminPasswordReset(email.trim());
      setIsLoading(false);
      setChallengeToken(response.challengeToken || '');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      switchMode('resetVerify');
      toast.success('Enter your authenticator code and new password');
    } catch (submitError) {
      setIsLoading(false);
      setError(submitError?.message || 'Unable to start password reset');
    }
  };

  const handleResetComplete = async (e) => {
    e.preventDefault();
    setError('');

    if (!challengeToken || !code.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setError('Please complete all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      await completeAdminPasswordReset({
        challengeToken,
        code: code.trim(),
        newPassword
      });
      setIsLoading(false);
      setPassword('');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      setChallengeToken('');
      switchMode('login');
      toast.success('Password updated. Sign in with your new password');
    } catch (submitError) {
      setIsLoading(false);
      setError(submitError?.message || 'Failed to reset password');
    }
  };

  return (
    <div className="auth-page admin-login-page">
      <div className="admin-login-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-login-card glass-card"
        >
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>

          <div className="admin-login-header">
            <div className="admin-logo-icon">
              <BrandLogo height={48} />
            </div>
            <h1>Admin Panel</h1>
            <p>Password + 2FA required</p>
          </div>

          {mode === 'login' && (
            <form onSubmit={handleLoginStart} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="email">Owner Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="owner@email.com"
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Continue
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => switchMode('resetStart')}
                disabled={isLoading}
              >
                Reset Password (2FA)
              </button>
            </form>
          )}

          {mode === 'twoFactor' && (
            <form onSubmit={handleTwoFactorVerify} className="admin-login-form">
              {setup && (
                <div className="admin-login-hint">
                  <p>Set up your authenticator app using this secret:</p>
                  <p><strong>{setup.secret}</strong></p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="twofa-code">2FA Code</label>
                <input
                  type="text"
                  id="twofa-code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError('');
                  }}
                  inputMode="numeric"
                  placeholder="6-digit code"
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Checking code...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Sign In
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => switchMode('login')}
                disabled={isLoading}
              >
                Back
              </button>
            </form>
          )}

          {mode === 'resetStart' && (
            <form onSubmit={handleResetStart} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="reset-email">Owner Email</label>
                <input
                  type="email"
                  id="reset-email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="owner@email.com"
                  autoComplete="email"
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Starting...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Continue
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => switchMode('login')}
                disabled={isLoading}
              >
                Back to Sign In
              </button>
            </form>
          )}

          {mode === 'resetVerify' && (
            <form onSubmit={handleResetComplete} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="reset-code">2FA Code</label>
                <input
                  type="text"
                  id="reset-code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError('');
                  }}
                  inputMode="numeric"
                  placeholder="6-digit code"
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <input
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setError('');
                  }}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Lock size={18} />
                    Update Password
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary w-full"
                onClick={() => switchMode('login')}
                disabled={isLoading}
              >
                Back to Sign In
              </button>
            </form>
          )}

          <div className="admin-login-footer">
            <div className="security-badge">
              <Shield size={14} />
              <span>2FA required for every admin login</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
